"use client";

import { useState, useCallback } from "react";
import type { User } from "@supabase/supabase-js";
import type { SupabaseClient } from "@supabase/supabase-js";
import { localePath, routes } from "@/lib/routes";
const IMPERSONATION_KEY = "opm_impersonation";

/**
 * Impersonation state stored in sessionStorage
 */
interface ImpersonationState {
  targetUserId: string;
  targetUserEmail: string;
  targetUserName: string;
  adminEmail: string;
  adminAccessToken: string;
  adminRefreshToken: string;
  startedAt: string;
}
export interface UseAuthAdminReturn {
  isAdmin: boolean;
  isImpersonating: boolean;
  impersonatedUser: ImpersonationState | null;
  startImpersonation: (targetUser: {
    id: string;
    email: string;
    name: string;
  }, redirectTo?: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  /** Reset admin + impersonation state (on sign-out) */
  resetAdmin: () => void;
  /** Check admin status for a given user (called on auth init / state change) */
  checkAndSetAdmin: (userId: string) => Promise<void>;
  /** Restore impersonation from sessionStorage if applicable */
  restoreImpersonation: (currentUser: User) => void;
}

/**
 * Manages admin status and impersonation lifecycle.
 */
export function useAuthAdmin(user: User | null, supabase: SupabaseClient): UseAuthAdminReturn {
  const [isAdmin, setIsAdmin] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonationState | null>(null);

  // Check admin status from DB
  const checkAdminStatus = useCallback(async (userId: string): Promise<boolean> => {
    try {
      const {
        data
      } = await supabase.from("users").select("is_admin").eq("id", userId).maybeSingle();
      return data?.is_admin === true;
    } catch {
      return false;
    }
  }, [supabase]);
  const checkAndSetAdmin = useCallback(async (userId_0: string) => {
    const result = await checkAdminStatus(userId_0);
    setIsAdmin(result);
  }, [checkAdminStatus]);
  const restoreImpersonation = useCallback((currentUser: User) => {
    if (typeof window === "undefined") return;
    const stored = sessionStorage.getItem(IMPERSONATION_KEY);
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored) as ImpersonationState;
      if (parsed.targetUserId === currentUser.id) {
        setImpersonatedUser(parsed);
      } else {
        sessionStorage.removeItem(IMPERSONATION_KEY);
      }
    } catch {
      sessionStorage.removeItem(IMPERSONATION_KEY);
    }
  }, []);
  const resetAdmin = useCallback(() => {
    setIsAdmin(false);
    setImpersonatedUser(null);
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(IMPERSONATION_KEY);
    }
  }, []);

  // Impersonation controls
  const startImpersonation = useCallback(async (targetUser: {
    id: string;
    email: string;
    name: string;
  }, redirectTo?: string) => {
    if (!user || !isAdmin) {
      throw new Error("Only platform admins can impersonate users");
    }
    if (targetUser.id === user.id) {
      throw new Error("Cannot impersonate this user");
    }
    const {
      data: {
        session: adminSession
      }
    } = await supabase.auth.getSession();
    if (!adminSession) {
      throw new Error("No active admin session");
    }
    const response = await fetch("/api/admin/impersonation", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        action: "start",
        targetUserId: targetUser.id
      })
    });
    if (!response.ok) {
      const data_0 = await response.json();
      throw new Error(data_0.error || "Failed to start impersonation");
    }
    const {
      session: targetSession
    } = await response.json();
    const state: ImpersonationState = {
      targetUserId: targetUser.id,
      targetUserEmail: targetUser.email,
      targetUserName: targetUser.name,
      adminEmail: user.email || "",
      adminAccessToken: adminSession.access_token,
      adminRefreshToken: adminSession.refresh_token,
      startedAt: new Date().toISOString()
    };
    sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(state));
    await supabase.auth.setSession({
      access_token: targetSession.access_token,
      refresh_token: targetSession.refresh_token
    });
    window.location.href = redirectTo || "/";
  }, [user, isAdmin, supabase]);
  const stopImpersonation = useCallback(async () => {
    if (!impersonatedUser) return;
    await supabase.auth.setSession({
      access_token: impersonatedUser.adminAccessToken,
      refresh_token: impersonatedUser.adminRefreshToken
    });
    sessionStorage.removeItem(IMPERSONATION_KEY);
    setImpersonatedUser(null);
    try {
      await fetch("/api/admin/impersonation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          action: "stop",
          targetUserId: impersonatedUser.targetUserId,
          adminAccessToken: impersonatedUser.adminAccessToken
        })
      });
    } catch (error) {
      console.error("Failed to log impersonation stop:", error);
    }
    window.location.href = localePath(routes.admin.users.list);
  }, [impersonatedUser, supabase]);
  return {
    isAdmin,
    isImpersonating: !!impersonatedUser,
    impersonatedUser,
    startImpersonation,
    stopImpersonation,
    resetAdmin,
    checkAndSetAdmin,
    restoreImpersonation
  };
}